<template>
  <div class="user-panel">
    <WalletComponent
      class="wallet-wrapper"
      :walletMoney="parseFloat(wallet)"
      :walletSumOfAllTime="parseFloat(walletSumOfAllTime)"
      :registeredCommissions="registeredCommissions"
      :bookingCommissions="bookingCommissions"
      :withdrawRequestsList="withdrawRequests"
      :withdrawalRequestsSumAmmount="withdrawalRequestsSumAmmount"
    />
    <div class="user-panel__tabs-wrapper">
      <q-tabs
        v-model="tab"
        :vertical="isMobile ? false : true"
        class="text-white"
        align="justify"
        :outside-arrows="isMobile"
        :mobile-arrows="isMobile"
      >
        <q-tab
          name="profile"
          icon="account_circle"
          :label="$t('userPanel.title')"
        />
        <q-tab
          name="images"
          icon="images"
          :label="$t('userPanel.addedPhoto')"
        />
        <q-tab
          v-if="userCanSeePhotographersList"
          name="photographers-list"
          icon="list"
          :label="$t('bookings.photographers')"
        />
        <q-tab
          v-if="userCanViewBookings"
          name="bookings"
          icon="list"
          :label="$t('bookings.myBookings')"
        />
        <q-tab
          v-if="userIsOrganizer"
          name="register-photographer"
          icon="person_add"
          :label="$t('userPanel.registerPhotographer')"
        />
        <q-tab
          v-if="userIsPhotographer"
          name="settings"
          icon="settings"
          :label="$t('userPanel.settings.title')"
        />
        <q-tab
          v-if="!isAdministrator"
          name="integration"
          icon="cable"
          :label="$t('userPanel.integration.title')"
        />
        <q-tab
          v-if="user && user.role === 'administrator'"
          name="addVideo"
          icon="movie"
          :label="$t('navigation.addVideo')"
        />
      </q-tabs>

      <q-tab-panels
        keep-alive
        v-model="tab"
        animated
        :vertical="isMobile ? false : true"
        transition-prev="jump-up"
        transition-next="jump-up"
      >
        <q-tab-panel name="profile">
          <UserCard />
        </q-tab-panel>

        <q-tab-panel name="images" class="q-px-sm">
          <div class="text-h4 q-pb-lg q-pt-md text-center user-panel__title">
            {{ $t("userPanel.addedPhoto") }}
          </div>
          <ImagesTable />
        </q-tab-panel>

        <q-tab-panel
          v-if="userCanSeePhotographersList"
          name="photographers-list"
          class="q-px-sm"
        >
          <PhotographersListing :canBook="userCanBookPhotographers" />
        </q-tab-panel>

        <q-tab-panel v-if="userCanViewBookings" name="bookings" class="q-px-sm">
          <BookingsPageRouter />
        </q-tab-panel>

        <q-tab-panel v-if="userIsOrganizer" name="register-photographer" class="q-px-sm">
          <div class="text-h4 q-pb-lg q-pt-md text-center user-panel__title">
            {{ $t('userPanel.registerPhotographer') }}
          </div>
          <RegisterPhotographerForm />
        </q-tab-panel>

        <q-tab-panel name="settings" class="q-px-sm">
          <SettingsCardWrapper />
        </q-tab-panel>

        <q-tab-panel v-if="!isAdministrator" name="integration" class="q-px-sm">
          <div class="text-h4 q-pb-lg q-pt-md text-center user-panel__title">
            {{ $t("userPanel.integration.title") }}
          </div>
          <IntegrationCard />
        </q-tab-panel>
      </q-tab-panels>
    </div>
  </div>
</template>

<script>
import ImagesTable from "./components/ImagesTable.vue";
import UserCard from "./components/UserCard.vue";
import WalletComponent from "./components/WalletComponent.vue";
import SettingsCardWrapper from "./components/SettingsCardWrapper.vue";
import IntegrationCard from "./components/IntegrationCard.vue";
import RegisterPhotographerForm from "./components/RegisterPhotographerForm.vue";
import PhotographersListing from "@/pages/bookings/components/PhotographersListing.vue";
import BookingsPageRouter from "@/pages/bookings/BookingsPageRouter.vue";
import { mapState, mapActions } from "vuex";

export default {
  name: "UserPanelPage",
  components: {
    ImagesTable,
    UserCard,
    WalletComponent,
    IntegrationCard,
    SettingsCardWrapper,
    PhotographersListing,
    BookingsPageRouter,
    RegisterPhotographerForm
  },
  props: {
    defaultTab: {
      type: String,
      default: "profile",
    },
  },
  data() {
    return {
      tab: this.defaultTab,
      splitterModel: 20,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      wallet: (state) => state.global.wallet,
      walletSumOfAllTime: (state) => state.global.walletSumOfAllTime,
      registeredCommissions: (state) => state.global.registeredCommissions,
      bookingCommissions: (state) => state.global.bookingCommissions,
      withdrawRequests: (state) => state.global.withdrawRequests,
      withdrawalRequestsSumAmmount: (state) =>
        state.global.withdrawalRequestsSumAmmount,
    }),
    isMobile() {
      return this.$q.screen.width < 780;
    },
    isAdministrator() {
      return this.user?.role === "administrator";
    },
    userCanSeePhotographersList() {
      return ["administrator", "organizer"].includes(this.user?.role);
    },
    userCanBookPhotographers() {
      return ["organizer"].includes(this.user?.role);
    },
    userIsOrganizer() {
      return ["organizer"].includes(this.user?.role);
    },
    userCanViewBookings() {
      return ["organizer", "photographer"].includes(this.user?.role);
    },
    userIsPhotographer() {
      return ["photographer"].includes(this.user?.role);
    },
  },
  watch: {
    tab(newTab) {
      if (this.$route.query.tab === newTab) {
        return;
      }
      if (newTab === "addVideo") {
        this.$router.push("/add-video");
        return;
      }
      this.$router
        .push({
          query: { tab: newTab },
        })
        .catch(() => {});
    },
    "$route.query.tab"(newTab) {
      if (!newTab) return;

      const tabAccess = {
        "profile": true,
        "images": true,
        "settings": this.userIsPhotographer,
        "photographers-list": this.userCanSeePhotographersList,
        "register-photographer": this.userIsOrganizer,
        "bookings": this.userCanViewBookings,
        "integration": !this.isAdministrator,
      };

      if (tabAccess[newTab] === true) {
        this.tab = newTab;
      } else if (tabAccess[newTab] === false) {
        this.$router.replace({ path: '/userPanel' });
      }
    },
  },
  methods: {
    ...mapActions({
      getUserWalletData: "global/getUserWalletData",
      getWithdrawRequestList: "global/getWithdrawRequestList",
    }),
  },
  beforeMount() {
    this.getUserWalletData();
    this.getWithdrawRequestList();
  },
};
</script>

<style lang="scss" scoped>
.wallet-wrapper {
  z-index: 10;
  width: 100%;
}
.user-panel {
  padding-top: 150px;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  &__title {
    border-bottom: 1px solid $accent;
  }
  &__tabs-wrapper {
    display: flex;
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-wrap: wrap;

    :deep .q-tab__content > .q-icon {
      display: block;
    }
    :deep .q-tabs__arrow {
      margin-top: 10px;
    }
  }

  &__tabs-hint {
    width: 100%;
    font-size: 12px;
    text-align: right;
    color: #d3d3d3;
    padding: 4px 8px 0;
  }
}
.q-tabs {
  width: 20%;
}
.q-tab-panels {
  width: calc(100% - 20% - 15px);
  margin-right: 15px;
}
@media (max-width: 780px) {
  .q-tabs {
    width: 100%;
  }
  .q-tab-panels {
    width: 100%;
    margin-right: 0;
  }
}
</style>
